import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, ShoppingCart, Minus, Plus, ChevronLeft, Check, ZoomIn } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { createWhatsAppLink, createProductWhatsAppMessage } from "@/lib/whatsapp";
import { supabase } from "@/supabase";
import CustomerForm from "./CustomerForm";
import ImageZoom from "./ImageZoom";

interface ProductDetailsProps {
  productId: string;
  onBack?: () => void;
}

const stockLabels = {
  available: { text: "Em estoque", class: "stock-available" },
  low: { text: "Últimas unidades", class: "stock-low" },
  out_of_stock: { text: "Esgotado", class: "stock-out" },
};

const ProductDetails = ({ productId, onBack }: ProductDetailsProps) => {
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [showImageZoom, setShowImageZoom] = useState(false);

  // Buscar produto do Supabase
  const { data: product, isLoading, error } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", productId)
        .eq("is_active", true)
        .single();

      if (error) {
        console.error("Erro ao buscar produto:", error);
        throw error;
      }

      // Inicializar seleções padrão
      const colors = data.colors || [];
      const sizes = data.sizes || [];
      
      return {
        ...data,
        colors,
        sizes,
      };
    },
    enabled: !!productId,
  });

  // Inicializar seleções quando o produto carregar
  useState(() => {
    if (product) {
      if (product.colors && product.colors.length > 0 && !selectedColor) {
        setSelectedColor(product.colors[0]);
      }
      if (product.sizes && product.sizes.length > 0 && !selectedSize) {
        setSelectedSize(product.sizes[0]);
      }
    }
  });

  const handleCheckout = () => {
    setShowCheckoutForm(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container-narrow">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ChevronLeft className="w-5 h-5" />
              Voltar para produtos
            </button>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image skeleton */}
            <div className="space-y-4">
              <div className="aspect-square bg-muted rounded-lg animate-pulse" />
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="aspect-square bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            </div>
            
            {/* Content skeleton */}
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="h-8 bg-muted rounded animate-pulse" />
                <div className="h-6 bg-muted rounded w-1/3 animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container-narrow">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ChevronLeft className="w-5 h-5" />
              Voltar para produtos
            </button>
          )}
          
          <div className="text-center py-20">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Produto não encontrado
            </h2>
            <p className="text-muted-foreground">
              O produto que você está procurando não existe ou foi removido.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isOutOfStock = !product.is_on_demand && (product.stock_status === "out_of_stock" || product.stock_quantity <= 0);
  const stockInfo = stockLabels[product.stock_status as keyof typeof stockLabels];
  const totalPrice = product.price * quantity;

  // Se o formulário estiver aberto, mostrar apenas ele
  if (showCheckoutForm) {
    // Criar item simples para enviar ao formulário
    const orderItem = {
      product_id: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      color: selectedColor,
      size: selectedSize,
      image_url: product.image_url,
    };

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          <CustomerForm
            items={[orderItem]}
            checkoutUrl={product.pagseguro_link || ""}
            onCancel={() => setShowCheckoutForm(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container-narrow">
        {/* Back Button */}
        {onBack && (
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ChevronLeft className="w-5 h-5" />
            Voltar para produtos
          </motion.button>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div 
              className="relative aspect-square overflow-hidden rounded-lg bg-muted cursor-zoom-in group"
              onClick={() => product.image_url && setShowImageZoom(true)}
            >
              {product.image_url ? (
                <>
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {/* Zoom indicator */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                      <ZoomIn className="w-6 h-6 text-gray-900" />
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ShoppingCart className="w-16 h-16 text-muted-foreground" />
                </div>
              )}
              {/* Stock Badge */}
              <div className="absolute top-4 right-4 z-10">
                {product.is_on_demand ? (
                  <span className="badge-on-demand">Sob encomenda</span>
                ) : (
                  <span className={stockInfo.class}>{stockInfo.text}</span>
                )}
              </div>
            </div>
            
            {product.image_url && (
              <p className="text-xs text-center text-muted-foreground">
                Clique na imagem para ampliar
              </p>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Title and Price */}
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-3">
                {product.name}
              </h1>
              <div className="flex items-baseline gap-3">
                <p className="text-4xl font-bold text-primary">
                  R$ {product.price.toFixed(2).replace(".", ",")}
                </p>
                {quantity > 1 && (
                  <p className="text-lg text-muted-foreground">
                    Total: R$ {totalPrice.toFixed(2).replace(".", ",")}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <div className="prose prose-sm max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  {product.description}
                </p>
              </div>
            )}

            <div className="decoration-line max-w-xs" />

            {/* Color Selection */}
            {product.colors && product.colors.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-foreground">
                  Cor: <span className="text-primary">{selectedColor}</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color: string) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 rounded-full border-2 transition-all ${
                        selectedColor === color
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-foreground">
                  Tamanho: <span className="text-primary">{selectedSize}</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size: string) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 rounded-full border-2 transition-all min-w-[60px] ${
                        selectedSize === size
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3 pt-4">
              {isOutOfStock ? (
                <button
                  disabled
                  className="w-full flex items-center justify-center gap-2 bg-muted text-muted-foreground py-4 rounded-full font-medium cursor-not-allowed text-lg"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Esgotado
                </button>
              ) : (
                <button
                  onClick={handleCheckout}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-4 rounded-full font-medium transition-all hover:opacity-90 text-lg relative"
                >
                  {addedToCart ? (
                    <>
                      <Check className="w-5 h-5" />
                      Redirecionando ao checkout!
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Fazer pedido 
                    </>
                  )}
                </button>
              )}
              
              <a
                href={createWhatsAppLink(
                  createProductWhatsAppMessage(
                    product.name,
                    product.price,
                    selectedColor,
                    selectedSize,
                    quantity
                  )
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 border-2 border-green-600 text-green-600 py-4 rounded-full font-medium transition-all hover:bg-green-50 text-lg"
              >
                <MessageCircle className="w-5 h-5" />
                Consultar no WhatsApp
              </a>
            </div>

            {/* Product Details */}
            <div className="bg-muted/30 rounded-lg p-6 space-y-3">
              <h3 className="font-semibold text-foreground mb-4">
                Detalhes do Produto
              </h3>
              <div className="space-y-2 text-sm">
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium">
                    {product.is_on_demand ? "Sob encomenda" : stockInfo.text}
                  </span>
                </p>
                {product.colors && product.colors.length > 0 && (
                  <p className="flex justify-between">
                    <span className="text-muted-foreground">Cores disponíveis:</span>
                    <span className="font-medium">{product.colors.length}</span>
                  </p>
                )}
                {product.sizes && product.sizes.length > 0 && (
                  <p className="flex justify-between">
                    <span className="text-muted-foreground">Tamanhos disponíveis:</span>
                    <span className="font-medium">{product.sizes.length}</span>
                  </p>
                )}
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Material:</span>
                  <span className="font-medium">100% Crochê Artesanal</span>
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <p className="text-sm text-muted-foreground text-center">
                Após a confirmação da compra, você receberá uma mensagem no WhatsApp com todos os detalhes do seu pedido e o código de rastreio. Pode ficar tranquilo: cuidamos de cada etapa para que sua peça chegue perfeita e com segurança até você ✨📦
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {product.image_url && (
        <ImageZoom
          images={[product.image_url]}
          currentIndex={0}
          open={showImageZoom}
          onClose={() => setShowImageZoom(false)}
          alt={product.name}
        />
      )}
    </div>
  );
};

export default ProductDetails;